package com.bikompanion.ui.composables

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableLongStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import androidx.compose.runtime.withFrameMillis
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.drawscope.scale
import androidx.compose.ui.graphics.drawscope.translate
import androidx.compose.ui.graphics.vector.VectorPainter
import androidx.compose.ui.graphics.vector.rememberVectorPainter
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.TextMeasurer
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.drawText
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.flow.collectLatest
import org.jetbrains.compose.resources.Font
import org.maplibre.compose.map.MapState
import org.maplibre.spatialk.geojson.Position
import com.bikompanion.domain.BikeNetwork
import com.bikompanion.domain.BikeParkingSpot
import com.bikompanion.domain.BikeStation
import com.bikompanion.domain.ParkingType
import com.bikompanion.ui.Res
import com.bikompanion.ui.icons.BikeRack
import com.bikompanion.ui.icons.FontAwesomeBuilding
import com.bikompanion.ui.icons.FontAwesomeWarehouse
import com.bikompanion.ui.icons.MaterialLock
import com.bikompanion.ui.models.ParkingAreaSummary
import com.bikompanion.ui.noto_sans_arabic
import com.bikompanion.ui.noto_sans_jp
import com.bikompanion.ui.noto_sans_kr
import com.bikompanion.ui.noto_sans_sc
import com.bikompanion.ui.preview.PreviewEmptyStation
import com.bikompanion.ui.preview.PreviewStation
import com.bikompanion.ui.preview.PreviewSurface
import com.bikompanion.ui.strings.BikompanionStrings
import com.bikompanion.ui.strings.LocalStrings
import kotlin.time.ComparableTimeMark
import kotlin.time.Duration.Companion.milliseconds

internal val StationDotDiameter = 8.dp
internal val StationCircleDiameter = 28.dp
private val NetworkCircleDiameter = 16.dp

/**
 * The clustered dot stays small on purpose — a city has tens of racks for every dock, and at
 * station size they would read as the more important of the two rather than the less. The
 * up-close circle is a different question: it now has to carry a type glyph legibly, so it
 * matches [StationCircleDiameter] rather than staying small for its own sake.
 */
private val ParkingDotDiameter = 6.dp
private val ParkingCircleDiameter = StationCircleDiameter
internal val ParkingSpotColor = Color(0xFF10B981)

private val LabelPaddingH = 8.dp
private val LabelPaddingV = 4.dp
private val LabelCornerRadius = 8.dp
private val LabelGap = 4.dp

// Markers whose centre falls this far outside the viewport are skipped entirely; the margin
// covers the ones straddling the edge, whose pie or label still reaches into view.
private val CullMargin = 80.dp

// A station whose numbers just moved swells briefly and throws off a ring. Availability
// changes without anything on the map moving, so without this a refresh is invisible.
private val ChangeFlashDuration = 1200.milliseconds
private val FlashRingWidth = 1.5.dp
private const val FlashRingGrowth = 2.6f
private const val FlashPop = 0.4f

/** A script whose glyphs the default font can't draw, so a bundled fallback is needed. */
private enum class Script { Japanese, Korean, Chinese, Arabic }

/**
 * Which [Script] a station name needs a fallback font for, or null if the default font already
 * covers it.
 *
 * Kana, Hangul and the Arabic block each identify their language on their own. Bare CJK
 * ideographs don't — most Japanese place names are kanji-only, with no kana in sight — so those
 * fall back to the station's network country, defaulting to Chinese where that's unknown.
 */
private fun requiredScript(
    name: String,
    networkCountry: String?,
): Script? {
    var hasKana = false
    var hasHangul = false
    var hasArabic = false
    var hasHan = false
    for (ch in name) {
        val cp = ch.code
        when {
            cp in 0x3040..0x30FF -> hasKana = true

            cp in 0xAC00..0xD7A3 || cp in 0x1100..0x11FF || cp in 0x3130..0x318F -> hasHangul = true

            cp in 0x0600..0x06FF || cp in 0x0750..0x077F || cp in 0xFB50..0xFDFF ||
                cp in 0xFE70..0xFEFF -> hasArabic = true

            cp in 0x4E00..0x9FFF -> hasHan = true
        }
    }
    return when {
        hasKana -> Script.Japanese
        hasHangul -> Script.Korean
        hasArabic -> Script.Arabic
        hasHan -> if (networkCountry == "JP") Script.Japanese else Script.Chinese
        else -> null
    }
}

/**
 * Scripts seen so far across every station name that has ever loaded, growing but never
 * shrinking so a font already fetched isn't dropped and re-fetched on the next pan.
 */
@Composable
private fun rememberNeededScripts(
    stations: List<BikeStation>,
    countryByNetwork: Map<String, String>,
): Set<Script> {
    var needed by remember { mutableStateOf(emptySet<Script>()) }
    LaunchedEffect(stations, countryByNetwork) {
        val found = stations.mapNotNullTo(mutableSetOf()) {
            requiredScript(it.name, countryByNetwork[it.networkId])
        }
        if (!needed.containsAll(found)) needed = needed + found
    }
    return needed
}

/** Loads a script's font the first time it's actually needed, and never before. */
@Composable
private fun rememberScriptFonts(needed: Set<Script>): Map<Script, FontFamily> {
    val fonts = mutableMapOf<Script, FontFamily>()
    if (Script.Japanese in needed) fonts[Script.Japanese] = FontFamily(Font(Res.font.noto_sans_jp))
    if (Script.Korean in needed) fonts[Script.Korean] = FontFamily(Font(Res.font.noto_sans_kr))
    if (Script.Chinese in needed) fonts[Script.Chinese] = FontFamily(Font(Res.font.noto_sans_sc))
    if (Script.Arabic in needed) {
        fonts[Script.Arabic] = FontFamily(Font(Res.font.noto_sans_arabic))
    }
    return fonts
}

/**
 * Draws every map marker — networks, stations and labels — into a single Canvas.
 *
 * One composable rather than one per station is the whole point: a marker per station meant
 * thousands of layout nodes being recomposed and re-laid-out on every camera frame, which is
 * what made panning crawl. Here the camera is read inside the draw lambda, so a camera move
 * invalidates only the draw phase — no recomposition, no layout, no per-marker allocation.
 *
 * [markerScale] scales every marker about its own anchor, for the caller to shrink one layer
 * away and grow the next one in. A lambda rather than a value for the same reason the camera is
 * read inside the draw lambda: an animating scale would otherwise recompose this on every frame.
 */
@Composable
internal fun MapMarkersOverlay(
    cameraState: MapState,
    networks: List<BikeNetwork>,
    stations: List<BikeStation>,
    parkingSpots: List<BikeParkingSpot>,
    parkingAreas: List<ParkingAreaSummary>,
    changedAt: Map<String, ComparableTimeMark>,
    modifier: Modifier = Modifier,
    markerScale: () -> Float = { 1f },
) {
    // Sized generously: label text is stable per station, so nearly every measure is a cache hit.
    val textMeasurer = rememberTextMeasurer(cacheSize = 256)
    val labelStrings = LocalStrings.current

    // Vectors, not hand-drawn Paths: created once each (they cache their own drawing commands)
    // and referenced, never re-derived, from inside the draw phase below.
    val parkingIcons = ParkingIcons(
        building = rememberVectorPainter(FontAwesomeBuilding),
        shed = rememberVectorPainter(FontAwesomeWarehouse),
        lockers = rememberVectorPainter(MaterialLock),
        stands = rememberVectorPainter(BikeRack),
    )

    // The system font Canvas.drawText falls back to has no glyphs for most non-Latin scripts on
    // some targets (e.g. web), so station names in Japanese, Korean, Chinese or Arabic would draw
    // as blank tofu without this. Each script's font is only fetched once a loaded station name
    // actually needs it, so the common case (Latin-only names) never pays for any of them.
    val countryByNetwork = remember(networks) { networks.associate { it.id to it.country } }
    val neededScripts = rememberNeededScripts(stations, countryByNetwork)
    val scriptFonts = rememberScriptFonts(neededScripts)

    // Everything a station marker draws that the camera cannot change, kept between frames.
    val stationText = remember { StationTextCache() }

    // A flash runs against the clock rather than the map, so nothing else would invalidate the
    // draw while one plays. This ticks the frame only while the newest one is still running,
    // and reading `frame` inside the Canvas keeps that to the draw phase.
    var frame by remember { mutableLongStateOf(0L) }
    LaunchedEffect(changedAt) {
        snapshotFlow { changedAt.values.maxOrNull() }
            .collectLatest { newest ->
                if (newest == null) return@collectLatest
                while (newest.elapsedNow() < ChangeFlashDuration) {
                    withFrameMillis { frame = it }
                }
            }
    }

    Canvas(modifier = modifier) {
        frame // Read for its own sake: this is what redraws a flash between camera moves.
        val viewport = cameraState.viewport ?: return@Canvas
        // Read in the draw phase on purpose — see the note above.
        val zoom = cameraState.cameraPosition.zoom
        val showNames = showStationNamesAt(zoom)
        val margin = CullMargin.toPx()
        // Read in the draw phase too — a layer swap animates this every frame.
        val scale = markerScale()

        // Every station and rack ever loaded is in these lists, including cities left long behind,
        // so the geographic test comes first everywhere below: comparing two doubles is far
        // cheaper than asking the map where a coordinate lands, and it takes the count down to
        // what is in view before a single projection happens.
        val visible = VisibleBounds(viewport.visibleBounds)

        // Each layer answers a given zoom in its own terms, and on its own threshold. A network
        // circle says "there is a bike-share system here", which is no answer at all to "where can
        // I lock my bike", so the rack layer clusters rather than borrowing them.
        if (zoom < PARKING_SPOTS_ZOOM) {
            parkingAreas.forEach { area ->
                if (!visible.contains(area.latitude, area.longitude)) return@forEach
                val center = screenOffset(cameraState, area.longitude, area.latitude)
                if (center == null || center.isOffScreen(size, margin)) return@forEach
                atScale(scale, center) { drawParkingArea(center, area, textMeasurer) }
            }
        } else {
            val parkingDotRadius = ParkingDotDiameter.toPx() / 2f
            val parkingCircleDiameter = ParkingCircleDiameter.toPx()
            val showParkingCircles = zoom >= STATIONS_PIE_ZOOM
            parkingSpots.forEach { spot ->
                if (!visible.contains(spot.latitude, spot.longitude)) return@forEach
                val center = screenOffset(cameraState, spot.longitude, spot.latitude)
                if (center == null || center.isOffScreen(size, margin)) return@forEach
                atScale(scale, center) {
                    if (showParkingCircles) {
                        drawParkingCircle(spot, center, parkingCircleDiameter, showNames, textMeasurer, parkingIcons)
                    } else {
                        drawCircle(color = ParkingSpotColor, radius = parkingDotRadius, center = center)
                    }
                }
            }
        }

        if (zoom < STATIONS_VISIBLE_ZOOM) {
            val diameter = NetworkCircleDiameter.toPx()
            networks.forEach { network ->
                val center = screenOffset(cameraState, network.longitude, network.latitude)
                if (center == null || center.isOffScreen(size, margin)) return@forEach
                atScale(scale, center) {
                    drawCircle(
                        color = Color(0xFF2563EB).copy(alpha = 0.85f),
                        radius = diameter / 2f,
                        center = center,
                    )
                    drawCircle(
                        color = Color.White,
                        radius = diameter / 2f,
                        center = center,
                        style = Stroke(1.5.dp.toPx()),
                    )
                }
            }
        } else {
            val showCircles = zoom >= STATIONS_PIE_ZOOM
            val dotRadius = StationDotDiameter.toPx() / 2f
            val circleDiameter = StationCircleDiameter.toPx()

            stations.forEach { station ->
                if (!visible.contains(station)) return@forEach
                val center = screenOffset(cameraState, station.longitude, station.latitude)
                if (center == null || center.isOffScreen(size, margin)) return@forEach

                val flash = changedAt[station.id]?.flashProgress()
                // Settles from swollen back to its own size, so the eye catches the change
                // rather than the marker's steady state.
                val pop = if (flash == null) 1f else 1f + FlashPop * (1f - easeOut(flash))

                atScale(scale, center) {
                    if (showCircles) {
                        drawStationCircle(
                            station,
                            center,
                            circleDiameter * pop,
                            showNames,
                            textMeasurer,
                            stationText.of(station, countryByNetwork, scriptFonts, labelStrings),
                        )
                        if (flash != null) drawChangeFlash(center, circleDiameter / 2f * pop, flash, station)
                    } else {
                        drawStationDot(center, dotRadius * pop, station)
                        if (flash != null) drawChangeFlash(center, dotRadius * pop, flash, station)
                    }
                }
            }
        }
    }
}

/**
 * Draws a marker scaled about its own anchor, so it shrinks into the point it marks rather than
 * towards the corner of the screen.
 *
 * The transform covers the label and badge text too — scaling those by hand would mean measuring
 * every string again at every size, which would blow the [TextMeasurer] cache on the one frame
 * per animation where every string is new.
 *
 * At exactly full size the transform is skipped: it is applied per marker, and steady state is by
 * far the common case. Anything else — including the overshoot a bouncy spring goes through above
 * 1 — is drawn scaled.
 */
private inline fun DrawScope.atScale(
    scale: Float,
    pivot: Offset,
    block: DrawScope.() -> Unit,
) {
    if (scale == 1f) block() else scale(scale, pivot) { block() }
}

/**
 * How far through its flash a change is, or null once it has finished.
 *
 * Marks are never swept from the map they live in, so an expired one has to read as "no
 * flash" rather than as a stale one.
 */
private fun ComparableTimeMark.flashProgress(): Float? {
    val elapsed = elapsedNow()
    if (elapsed >= ChangeFlashDuration) return null
    return (elapsed / ChangeFlashDuration).toFloat()
}

/** A ring thrown off by a marker whose numbers just moved, expanding as it fades. */
private fun DrawScope.drawChangeFlash(
    center: Offset,
    baseRadius: Float,
    progress: Float,
    station: BikeStation,
) {
    val eased = easeOut(progress)
    drawCircle(
        // Matched to the marker's own colour so the ring reads as coming from it.
        color = stationDotColor(station),
        radius = baseRadius * lerp(
            1f,
            FlashRingGrowth,
            eased,
        ),
        center = center,
        alpha = 1f - eased,
        style = Stroke(FlashRingWidth.toPx()),
    )
}

/** Fast at the start, settling at the end — a ping rather than a slide. */
private fun easeOut(t: Float): Float {
    val clamped = t.coerceIn(0f, 1f)
    return 1f - (1f - clamped) * (1f - clamped)
}

/** The full reading of a station: capacity ring, bikes-available count, and its name. */
private fun DrawScope.drawStationCircle(
    station: BikeStation,
    center: Offset,
    diameter: Float,
    showName: Boolean,
    textMeasurer: TextMeasurer,
    text: StationMarkerText,
) {
    drawStationPie(
        center = center,
        diameterPx = diameter,
        normalBikes = station.normalBikes,
        electricBikes = station.electricBikes,
        emptySlots = station.emptySlots,
        detail = 1f,
    )

    drawCircle(
        color = Color.White.copy(alpha = 0.92f),
        radius = diameter * 0.3f,
        center = center,
    )
    // Always digits, so the default font is all it ever needs.
    val badge = textMeasurer.measure(text.badge, BadgeTextStyle)
    drawText(
        textLayoutResult = badge,
        topLeft = Offset(
            center.x - badge.size.width / 2f,
            center.y - badge.size.height / 2f,
        ),
    )

    if (showName) {
        drawStationLabel(
            label = text.label,
            top = center.y + diameter / 2f + LabelGap.toPx(),
            centerX = center.x,
            textMeasurer = textMeasurer,
        )
    }
}

/**
 * A station's marker text, held between frames.
 *
 * None of it depends on where the camera is looking, and building it in the draw phase is what
 * made a moving map stutter. A label is a dozen allocations — a builder, four [SpanStyle]s and
 * the strings for the counts — and the [TextMeasurer] cache cannot answer until one has been
 * built to look it up with, so a "cache hit" still cost a full rebuild plus a deep hash of the
 * result. Multiplied by every named station on screen, on every frame of every camera move.
 * [requiredScript] is here for the same reason: it walks every character of the name.
 */
private class StationMarkerText(
    val station: BikeStation,
    val script: Script?,
    val fontFamily: FontFamily?,
    val label: AnnotatedString,
    val badge: String,
)

/**
 * Keeps one [StationMarkerText] per station, rebuilt only when that station's own numbers move
 * or when the font its name needs finally finishes loading.
 *
 * Keyed by station id rather than by the station itself, so it holds one entry per station the
 * map has ever loaded rather than growing with every refresh that changed one.
 */
private class StationTextCache {
    private val entries = mutableMapOf<String, StationMarkerText>()

    fun of(
        station: BikeStation,
        countryByNetwork: Map<String, String>,
        scriptFonts: Map<Script, FontFamily>,
        strings: BikompanionStrings,
    ): StationMarkerText {
        val cached = entries[station.id]
        // The station carries its own name and counts, so its equality covers everything the
        // label is built from bar the font — which arrives later than the name that needs it.
        if (cached != null && cached.station == station) {
            val font = cached.script?.let { scriptFonts[it] }
            if (cached.fontFamily == font) return cached
        }
        val script = requiredScript(station.name, countryByNetwork[station.networkId])
        val fontFamily = script?.let { scriptFonts[it] }
        return StationMarkerText(
            station = station,
            script = script,
            fontFamily = fontFamily,
            label = stationLabelText(station, fontFamily, strings),
            badge = station.freeBikes.coerceAtLeast(0).toString(),
        ).also { entries[station.id] = it }
    }
}

/**
 * A patch of ground with racks in it, with how many, for zooms too far out to draw them singly.
 *
 * Sized by its count rather than drawn at a fixed size: pulled back over a country, what the
 * eye should pick out is where the racks actually are, and a hundred-rack city and a two-rack
 * village drawn identically say the opposite.
 */
private fun DrawScope.drawParkingArea(
    center: Offset,
    area: ParkingAreaSummary,
    textMeasurer: TextMeasurer,
) {
    val diameter = parkingAreaDiameterFor(area.spotCount).toPx()
    drawCircle(color = ParkingSpotColor.copy(alpha = 0.85f), radius = diameter / 2f, center = center)
    drawCircle(
        color = Color.White,
        radius = diameter / 2f,
        center = center,
        style = Stroke(1.5.dp.toPx()),
    )
    // Always digits, so the default font is all it ever needs.
    val label = textMeasurer.measure(area.spotCount.abbreviated(), ParkingAreaTextStyle)
    drawText(
        textLayoutResult = label,
        topLeft = Offset(center.x - label.size.width / 2f, center.y - label.size.height / 2f),
    )
}

/** Grows with the count but not in step with it, so a big city can't swallow the map. */
private fun parkingAreaDiameterFor(spotCount: Int) =
    when {
        spotCount >= 1000 -> 34.dp
        spotCount >= 250 -> 30.dp
        spotCount >= 50 -> 26.dp
        else -> 22.dp
    }

/** "1.2k" rather than "1204": at this zoom the order of magnitude is the whole message. */
private fun Int.abbreviated(): String =
    when {
        this >= 1000 -> "${this / 1000}.${(this % 1000) / 100}k"
        else -> toString()
    }

private val ParkingAreaTextStyle = TextStyle(
    fontSize = 10.sp,
    fontWeight = FontWeight.Bold,
    color = Color.White,
)

/**
 * A rack up close: a shape naming what kind of parking it is, carrying its capacity where
 * somebody has tagged one.
 *
 * Most racks have no capacity in OSM, so the number is the exception rather than the label —
 * an untagged rack draws as a plain shape rather than as a gap where a number should be.
 */
private fun DrawScope.drawParkingCircle(
    spot: BikeParkingSpot,
    center: Offset,
    diameter: Float,
    showCapacity: Boolean,
    textMeasurer: TextMeasurer,
    icons: ParkingIcons,
) {
    drawParkingMark(spot.type, center, diameter, icons)
    val capacity = spot.capacity?.takeIf { showCapacity && it > 0 } ?: return
    // Always digits, so the default font is all it ever needs.
    val label = textMeasurer.measure(capacity.toString(), ParkingCapacityTextStyle)
    drawText(
        textLayoutResult = label,
        topLeft = Offset(
            center.x - label.size.width / 2f,
            center.y + diameter / 2f + LabelGap.toPx(),
        ),
    )
}

/** The four glyphs a rack can carry, held together so a draw call takes one parameter, not four. */
internal class ParkingIcons(
    val building: VectorPainter,
    val shed: VectorPainter,
    val lockers: VectorPainter,
    val stands: VectorPainter,
)

/**
 * The mark drawn for a rack: the same green-filled, white-ringed dot every rack has always drawn
 * as, with a small white glyph laid inside it naming the [ParkingType].
 *
 * [ParkingType.Other] borrows the stand glyph rather than going bare — untagged parking is
 * overwhelmingly bare stands in practice, and a plain undecorated dot next to four glyphed types
 * would read as "broken" rather than as "unknown".
 */
private fun DrawScope.drawParkingMark(
    type: ParkingType,
    center: Offset,
    diameter: Float,
    icons: ParkingIcons,
) {
    val radius = diameter / 2f
    drawCircle(color = ParkingSpotColor, radius = radius, center = center)
    drawCircle(color = Color.White, radius = radius, center = center, style = Stroke(1.5.dp.toPx()))

    val icon = when (type) {
        ParkingType.Other, ParkingType.Stands -> icons.stands
        ParkingType.Shed -> icons.shed
        ParkingType.Building -> icons.building
        ParkingType.Lockers -> icons.lockers
    }
    drawVectorIcon(icon, center, diameter)
}

/** A glyph tinted to match the ring's white, and centred inside it. */
private fun DrawScope.drawVectorIcon(
    icon: VectorPainter,
    center: Offset,
    diameter: Float,
) {
    val iconSize = diameter * 0.5f
    translate(left = center.x - iconSize / 2f, top = center.y - iconSize / 2f) {
        with(icon) { draw(size = Size(iconSize, iconSize), colorFilter = ColorFilter.tint(Color.White)) }
    }
}

private val ParkingCapacityTextStyle = TextStyle(
    fontSize = 10.sp,
    fontWeight = FontWeight.Bold,
    color = Color(0xFF047857),
)

private fun DrawScope.drawStationLabel(
    label: AnnotatedString,
    top: Float,
    centerX: Float,
    textMeasurer: TextMeasurer,
) {
    val layout = textMeasurer.measure(label, LabelTextStyle)
    val paddingH = LabelPaddingH.toPx()
    val paddingV = LabelPaddingV.toPx()
    val width = layout.size.width + paddingH * 2
    val height = layout.size.height + paddingV * 2
    val left = centerX - width / 2f

    drawRoundRect(
        color = Color.White,
        topLeft = Offset(left, top),
        size = Size(width, height),
        cornerRadius = CornerRadius(LabelCornerRadius.toPx()),
    )
    drawText(
        textLayoutResult = layout,
        topLeft = Offset(left + paddingH, top + paddingV),
    )
}

private val BadgeTextStyle = TextStyle(
    fontSize = 11.sp,
    fontWeight = FontWeight.Bold,
    color = Color.DarkGray,
)

private val LabelTextStyle = TextStyle(textAlign = TextAlign.Center)

private fun stationLabelText(
    station: BikeStation,
    fontFamily: FontFamily?,
    strings: BikompanionStrings,
): AnnotatedString =
    buildAnnotatedString {
        withStyle(
            SpanStyle(
                fontSize = 11.sp,
                fontWeight = FontWeight.Bold,
                color = Color.DarkGray,
                fontFamily = fontFamily,
            ),
        ) {
            append(station.name)
        }
        append("\n")
        withStyle(
            SpanStyle(
                fontSize = 9.sp,
                fontWeight = FontWeight.SemiBold,
                color = NormalBikeColor,
                fontFamily = fontFamily,
            ),
        ) {
            append(strings.mapLabelBikes(station.normalBikes))
        }
        if (station.electricBikes > 0) {
            withStyle(
                SpanStyle(
                    fontSize = 9.sp,
                    fontWeight = FontWeight.SemiBold,
                    color = ElectricBikeColor,
                    fontFamily = fontFamily,
                ),
            ) {
                append("  " + strings.mapLabelEbikes(station.electricBikes))
            }
        }
        withStyle(SpanStyle(fontSize = 9.sp, color = Color.Gray, fontFamily = fontFamily)) {
            append("  " + strings.mapLabelFree(station.emptySlots))
        }
    }

/**
 * The viewport in degrees, grown enough to keep markers that straddle its edge.
 *
 * Padded by a quarter of the span rather than a figure in metres: it has to cover
 * [CullMargin], and how many degrees that is depends on zoom and latitude. Being generous
 * only costs a few projections that the screen-space test then discards anyway.
 */
private class VisibleBounds(
    box: org.maplibre.compose.util.VisibleBounds,
) {
    private val south: Double
    private val north: Double
    private val west: Double
    private val east: Double

    init {
        val latitudePad = (box.north - box.south) * 0.25
        val longitudePad = (box.east - box.west) * 0.25
        south = box.south - latitudePad
        north = box.north + latitudePad
        west = box.west - longitudePad
        east = box.east + longitudePad
    }

    fun contains(station: BikeStation) = contains(station.latitude, station.longitude)

    fun contains(
        latitude: Double,
        longitude: Double,
    ) = latitude in south..north && longitude in west..east
}

private fun DrawScope.screenOffset(
    cameraState: MapState,
    longitude: Double,
    latitude: Double,
): Offset? {
    val location = cameraState.viewport?.screenLocationFromPosition(
        Position(longitude = longitude, latitude = latitude),
    ) ?: return null
    return Offset(location.x.toPx(), location.y.toPx())
}

private fun Offset.isOffScreen(
    size: Size,
    margin: Float,
) = x < -margin || y < -margin || x > size.width + margin || y > size.height + margin

/** Every [ParkingType]'s marker, side by side, so a new glyph can be checked without the map. */
@Preview
@Composable
private fun ParkingTypeIconsPreview() {
    val icons = rememberPreviewParkingIcons()
    PreviewSurface {
        Row(horizontalArrangement = Arrangement.spacedBy(16.dp), modifier = Modifier.padding(16.dp)) {
            ParkingType.entries.forEach { type ->
                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                    Canvas(modifier = Modifier.size(ParkingCircleDiameter + 8.dp)) {
                        drawParkingMark(
                            type,
                            center = Offset(size.width / 2f, size.height / 2f),
                            diameter = ParkingCircleDiameter.toPx(),
                            icons = icons,
                        )
                    }
                    Text(type.name, fontSize = 10.sp)
                }
            }
        }
    }
}

// The overlay itself needs a live map to project onto, so the previews below draw each of its
// markers straight onto a Canvas instead.

/** A tagged rack with its capacity showing, with it hidden at this zoom, and an untagged one. */
@Preview
@Composable
private fun ParkingCirclePreview() {
    val icons = rememberPreviewParkingIcons()
    val textMeasurer = rememberTextMeasurer()
    PreviewSurface {
        Row(horizontalArrangement = Arrangement.spacedBy(16.dp), modifier = Modifier.padding(16.dp)) {
            listOf(12 to true, 12 to false, null to true).forEach { (capacity, showCapacity) ->
                Canvas(modifier = Modifier.size(ParkingCircleDiameter * 2)) {
                    drawParkingCircle(
                        spot = BikeParkingSpot(id = "spot", latitude = 0.0, longitude = 0.0, capacity = capacity),
                        center = Offset(size.width / 2f, ParkingCircleDiameter.toPx() / 2f),
                        diameter = ParkingCircleDiameter.toPx(),
                        showCapacity = showCapacity,
                        textMeasurer = textMeasurer,
                        icons = icons,
                    )
                }
            }
        }
    }
}

/** One size per step of [parkingAreaDiameterFor], the last abbreviated. */
@Preview
@Composable
private fun ParkingAreaPreview() {
    val textMeasurer = rememberTextMeasurer()
    PreviewSurface {
        Row(horizontalArrangement = Arrangement.spacedBy(16.dp), modifier = Modifier.padding(16.dp)) {
            listOf(12, 75, 300, 1204).forEach { spotCount ->
                Canvas(modifier = Modifier.size(40.dp)) {
                    drawParkingArea(
                        center = center,
                        area = ParkingAreaSummary(latitude = 0.0, longitude = 0.0, spotCount = spotCount),
                        textMeasurer = textMeasurer,
                    )
                }
            }
        }
    }
}

/** The full reading of a station, without and with the name it gains at a closer zoom. */
@Preview
@Composable
private fun StationCirclePreview() {
    val textMeasurer = rememberTextMeasurer()
    PreviewSurface {
        val strings = LocalStrings.current
        Column(verticalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.padding(16.dp)) {
            listOf(PreviewStation, PreviewEmptyStation).forEach { station ->
                val text = StationMarkerText(
                    station = station,
                    script = null,
                    fontFamily = null,
                    label = stationLabelText(station, fontFamily = null, strings),
                    badge = station.freeBikes.toString(),
                )
                Row(horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                    listOf(false, true).forEach { showName ->
                        Canvas(modifier = Modifier.size(width = 180.dp, height = 88.dp)) {
                            drawStationCircle(
                                station = station,
                                center = Offset(size.width / 2f, StationCircleDiameter.toPx() / 2f),
                                diameter = StationCircleDiameter.toPx(),
                                showName = showName,
                                textMeasurer = textMeasurer,
                                text = text,
                            )
                        }
                    }
                }
            }
        }
    }
}

/** A ring thrown off by a station whose numbers just moved, part of the way through it. */
@Preview
@Composable
private fun ChangeFlashPreview() {
    PreviewSurface {
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.padding(16.dp)) {
            listOf(0.1f, 0.3f, 0.6f).forEach { progress ->
                Canvas(modifier = Modifier.size(StationCircleDiameter * 2)) {
                    val radius = StationDotDiameter.toPx()
                    drawStationDot(center, radius, PreviewStation)
                    drawChangeFlash(center, radius, progress, PreviewStation)
                }
            }
        }
    }
}

@Composable
private fun rememberPreviewParkingIcons() =
    ParkingIcons(
        building = rememberVectorPainter(FontAwesomeBuilding),
        shed = rememberVectorPainter(FontAwesomeWarehouse),
        lockers = rememberVectorPainter(MaterialLock),
        stands = rememberVectorPainter(BikeRack),
    )
